import * as React from 'react';
import classnames from 'classnames';
import {
    SearchBox,
    CommandBarButton,
    TextField,
    DialogType,
    Dialog,
    IconButton,
    ContextualMenu,
} from '@fluentui/react';
import { List } from '@fluentui/react/lib/List';
import useDebouncedState from 'hooks/useDebouncedState';
import { ITheme, mergeStyleSets, getTheme, getFocusStyle } from '@fluentui/react/lib/Styling';
import { ValueType } from 'react-select';
import AutoSizer from 'react-virtualized-auto-sizer';
import { SelectOption } from 'formComponents/Select/types';
import { Select } from 'formComponents/Select/Select';
import { useKundeList, Kunde, KundeFilter } from 'api/kunde';
import { betreuerFilterItems, useBetreuer, useBetreuerByNr } from 'api/betreuer';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

const defaultMaxRows = 100;

const theme: ITheme = getTheme();
const { palette, semanticColors, fonts } = theme;

const styles = mergeStyleSets({
    textField: {
        cursor: 'pointer !important',
        textDecoration: 'underline',
    },
    container: {
        margin: 0,
        padding: '0 10px 10px 0',
        display: 'grid',
        gridTemplateRows: 'max-content max-content 400px',
        paddingRight: 10,
        borderRight: '1px solid rgb(96, 94, 92)',
    },
    itemCell: [
        getFocusStyle(theme, { inset: -1 }),
        {
            minHeight: 54,
            padding: '5px 0',
            boxSizing: 'border-box',
            borderBottom: `1px solid ${semanticColors.bodyDivider}`,
            display: 'flex',
            // width: 290,
            selectors: {
                '&:hover': { background: palette.neutralLight },
            },
        },
    ],
    clickable: {
        cursor: 'pointer',
    },
    active: {
        backgroundColor: '#CCC',
    },
    itemContent: {
        marginLeft: 10,
        overflow: 'hidden',
        flexGrow: 1,
    },
    itemName: [
        fonts.large,
        {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    ],
    itemIndex: {
        fontSize: fonts.small.fontSize,
        color: palette.neutralTertiary,
        marginBottom: 10,
    },
    chevron: {
        alignSelf: 'center',
        marginLeft: 10,
        color: palette.neutralTertiary,
        fontSize: fonts.large.fontSize,
        flexShrink: 0,
    },
    searchLine: {
        display: 'grid',
        gridTemplateColumns: '250px 120px 250px 1fr',
        gridColumnGap: 10,
    },
    searchTextFromBeginning: {
        marginTop: 6,
    },
    infoLine: {
        display: 'flex',
    },
    reset: {
        right: 10,
    },
    inputContainer: {
        position: 'relative',
    },
});

interface FilterValues {
    maxRows: number;
    nr: number | null;
    searchText: string;
    searchTextFromBeginning: boolean;
    betreuerFilter: SelectOption;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    nr: null,
    searchText: '',
    searchTextFromBeginning: false,
    betreuerFilter: betreuerFilterItems[0],
};

interface KundeSelectProps {
    title?: string;
    label: string;
    kNr?: number;
    name?: string;
    initFilterKNr?: number;
    disabled?: boolean;
    dialogOnly?: boolean;
    allowEmpty?: boolean;
    onChange: (newKNr?: number, newKunde?: Kunde) => void;
    onCancel?: () => void;
}

export const KundeSelect: React.FC<KundeSelectProps> = ({
    title = 'Kundenauswahl',
    label,
    kNr,
    name,
    initFilterKNr,
    disabled = false,
    dialogOnly = false,
    allowEmpty = false,
    onChange,
    onCancel,
}) => {
    const auth = useSelector(authSelector);
    const betreuer = useBetreuer();
    const betreuerByNr = useBetreuerByNr();

    const [isOpen, setIsOpen] = React.useState(dialogOnly);

    const betreuerFilterOptions = React.useMemo(() => {
        if (auth.rolle === 'AD') {
            return betreuerFilterItems;
        }

        return [
            ...betreuerFilterItems,
            ...betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map<SelectOption>((b) => ({ value: b.nr.toString(), label: b.name })),
        ];
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>({
        ...defaultFilterValues,
        nr: initFilterKNr || defaultFilterValues.nr,
    });

    const filter = React.useMemo<KundeFilter>(
        () => ({
            nr: debouncedFilterValues.nr,
            searchText: debouncedFilterValues.searchText,
            searchTextFromBeginning: debouncedFilterValues.searchTextFromBeginning,
            betreuer: parseInt(debouncedFilterValues.betreuerFilter.value, 10),
            maxRows: debouncedFilterValues.maxRows,
        }),
        [debouncedFilterValues],
    );

    const kundeList = useKundeList(filter, { enabled: false });

    React.useEffect(() => {
        if (isOpen) {
            kundeList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, isOpen]);

    const reset = () => {
        setFilterValues(defaultFilterValues);
    };

    const onRenderCell = (kunde?: Kunde): JSX.Element => {
        return (
            <div
                className={classnames(styles.itemCell, styles.clickable)}
                data-is-focusable
                onClick={() => {
                    setIsOpen(false);
                    if (kunde?.nr !== kNr) {
                        onChange(kunde?.nr, kunde);
                    }
                }}
            >
                <div className={styles.itemContent}>
                    <div className={styles.itemName}>
                        {kunde?.name1} {kunde?.name2}
                    </div>
                    <div className={styles.itemIndex}>
                        {kunde?.betreuer
                            ? betreuerByNr[kunde?.betreuer]?.name ||
                              betreuerByNr[kunde?.betreuer]?.kz ||
                              kunde?.betreuer
                            : ''}
                        {' / '} {kunde?.nr}
                    </div>
                    <div>
                        {kunde?.plz} {kunde?.ort}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.inputContainer}>
            <TextField
                styles={{ field: { cursor: disabled ? 'default' : 'pointer' } }}
                label={label}
                value={name || ''}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        setIsOpen(true);
                    }
                }}
            />
            {allowEmpty && (
                <IconButton
                    onClick={() => {
                        onChange(undefined, undefined);
                    }}
                    iconProps={{ iconName: 'Clear', style: { fontSize: 12 } }}
                    style={{ position: 'absolute', top: 37, right: 12, width: 15, height: 15 }}
                />
            )}
            {isOpen && (
                <Dialog
                    hidden={false}
                    title={title}
                    dialogContentProps={{
                        type: DialogType.normal,
                    }}
                    modalProps={{
                        isBlocking: true,
                        dragOptions: {
                            moveMenuItemText: 'Move',
                            closeMenuItemText: 'Close',
                            menu: ContextualMenu,
                        },
                    }}
                    minWidth={800}
                    onDismiss={() => {
                        if (dialogOnly && onCancel) {
                            onCancel();
                        } else {
                            setIsOpen(false);
                        }
                    }}
                >
                    <div className={styles.container}>
                        <div className={styles.searchLine}>
                            <Select
                                name="betreuer"
                                value={filterValues.betreuerFilter}
                                options={betreuerFilterOptions}
                                onChange={(value: ValueType<SelectOption, false>): void => {
                                    setFilterValues({
                                        ...filterValues,
                                        betreuerFilter: value as SelectOption,
                                    });
                                }}
                            />

                            <SearchBox
                                type="number"
                                value={filterValues.nr === null ? '' : filterValues.nr.toString()}
                                onChange={(event, newValue): void => {
                                    setFilterValues({
                                        ...filterValues,
                                        nr: newValue === '' || newValue === undefined ? null : parseInt(newValue, 10),
                                    });
                                }}
                                placeholder="Kundennr."
                            />

                            <SearchBox
                                value={filterValues.searchText}
                                onChange={(event, newValue): void => {
                                    setFilterValues({
                                        ...filterValues,
                                        searchText: newValue === undefined ? '' : newValue,
                                    });
                                }}
                                placeholder="Name"
                                autoFocus
                            />
                            <Checkbox
                                className={styles.searchTextFromBeginning}
                                label="Anfang"
                                checked={filterValues.searchTextFromBeginning}
                                inputProps={{
                                    style: {
                                        marginTop: -5,
                                        fontSize: 10,
                                    },
                                }}
                                onChange={(event, checked) => {
                                    setFilterValues({
                                        ...filterValues,
                                        searchTextFromBeginning: checked || false,
                                    });
                                }}
                            />
                        </div>
                        <div className={styles.reset}>
                            <CommandBarButton text="Zurücksetzen" onClick={reset} />
                        </div>
                        <div>
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}>
                                        <List items={kundeList.data || []} onRenderCell={onRenderCell} />
                                    </div>
                                )}
                            </AutoSizer>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
};
